import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 80;
const POLL_TIMEOUT_MS = 4000;

export interface TargetState {
  /**
   * The matched element, or null if it isn't on the page yet.
   *
   * We type as ``Element`` (not ``HTMLElement``) so SVG nodes — e.g. the
   * ``<g class="photo-marker">`` markers in the interactive viewer — work
   * as targets without TS gymnastics.
   */
  element: Element | null;
  /** Bounding rect (viewport-relative) or null when no element matched. */
  rect: DOMRect | null;
}

/**
 * Resolves a CSS selector to an element + bounding rect, and keeps the rect in
 * sync as the page scrolls / resizes / mutates. While the element is missing
 * we briefly poll for it (handles routes / dialogs that mount asynchronously).
 */
export function useTargetRect(selector: string | null): TargetState {
  const [state, setState] = useState<TargetState>({ element: null, rect: null });

  useEffect(() => {
    if (!selector) {
      setState({ element: null, rect: null });
      return;
    }

    let cancelled = false;
    let element: Element | null = null;
    let pollTimer: number | null = null;
    let resizeObs: ResizeObserver | null = null;
    let mutationObs: MutationObserver | null = null;

    const measure = () => {
      if (!element || cancelled) return;
      const rect = element.getBoundingClientRect();
      setState((curr) => {
        if (
          curr.element === element &&
          curr.rect &&
          curr.rect.x === rect.x &&
          curr.rect.y === rect.y &&
          curr.rect.width === rect.width &&
          curr.rect.height === rect.height
        ) {
          return curr;
        }
        return { element, rect };
      });
    };

    const attach = (el: Element) => {
      element = el;
      measure();

      resizeObs = new ResizeObserver(measure);
      resizeObs.observe(el);
      // The viewport itself may resize too.
      resizeObs.observe(document.documentElement);

      window.addEventListener('scroll', measure, true);
      window.addEventListener('resize', measure);
    };

    const tryFind = () => {
      const el = document.querySelector(selector);
      if (el) {
        attach(el);
        return true;
      }
      return false;
    };

    if (!tryFind()) {
      // Poll briefly while we wait for the element to mount.
      const start = Date.now();
      const poll = () => {
        if (cancelled) return;
        if (tryFind()) return;
        if (Date.now() - start > POLL_TIMEOUT_MS) {
          // Fall back to a MutationObserver in case the element appears later
          // (e.g. user opens a dialog manually).
          mutationObs = new MutationObserver(() => {
            if (tryFind() && mutationObs) {
              mutationObs.disconnect();
              mutationObs = null;
            }
          });
          mutationObs.observe(document.body, { childList: true, subtree: true });
          return;
        }
        pollTimer = window.setTimeout(poll, POLL_INTERVAL_MS);
      };
      poll();
    }

    return () => {
      cancelled = true;
      if (pollTimer !== null) window.clearTimeout(pollTimer);
      if (resizeObs) resizeObs.disconnect();
      if (mutationObs) mutationObs.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [selector]);

  return state;
}
